"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, Gauge, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PatSystemItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  tag: string;
  iconName: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  linkPath: string;
}
const patSystems: PatSystemItem[] = [{
  id: "raman",
  title: "Process Raman Systems",
  subtitle: "Real-time Molecular Fingerprinting",
  description: "In-line and on-line Raman spectroscopy systems engineered for non-destructive real-time monitoring of chemical composition, polymorph transitions, and bioprocess kinetics.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_raman_system.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_raman_system.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_raman_system.png-wvc-sizes",
  imageAlt: "Process Raman analytical instrumentation system for laboratory and process control",
  tag: "Non-Destructive PAT",
  iconName: "Activity",
  bullet1: "Continuous in-situ reaction monitoring and endpoint detection",
  bullet2: "High chemical specificity with fiber-coupled optical probes",
  bullet3: "Supports data integrity and 21 CFR Part 11 requirements",
  linkPath: "/ProductsPage"
}, {
  id: "nir",
  title: "Process NIR Systems",
  subtitle: "High-Speed Physical & Chemical Profiling",
  description: "Robust Near-Infrared spectroscopy platforms configured for solid, liquid, and slurry analysis across drying, blending, granulating, and continuous manufacturing lines.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_nir_system.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_nir_system.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_nir_system.png-wvc-sizes",
  imageAlt: "Process NIR spectrometer hardware installed on a pharmaceutical production unit",
  tag: "High-Throughput PAT",
  iconName: "Cpu",
  bullet1: "Rapid blend uniformity and moisture content verification",
  bullet2: "Direct interface into blenders, fluid beds, and feed frames",
  bullet3: "Seamless chemometric model transfer and lifecycle support",
  linkPath: "/SolutionsPage"
}];
export default function Systems() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="pathubsystems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/40">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="font-medium px-3 py-1">
              <Gauge className="size-3.5 text-primary" />
              PAT Hub Technology
            </Badge>
          </div>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Process Analytical Systems
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Application-led analytical technology configured for regulated laboratories and manufacturing teams across Ireland and the UK.
          </p>
        </div>

        {/* 2-Column Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {patSystems.map((system, i) => <div key={system.id} data-index={i} className="flex">
              <motion.div className="w-full flex" initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.5,
            delay: i * 0.15
          }}>
                <Card className="w-full bg-card text-card-foreground border border-border shadow-md hover:shadow-lg transition-shadow flex flex-col justify-between overflow-hidden rounded-xl">
                  <div>
                    {/* Visual Asset Container */}
                    <div className="relative aspect-16/9 w-full bg-muted overflow-hidden">
                      <img src={system.imageSrc} data-wvc-srcset={system.imageSrcset} data-wvc-sizes={system.imageSizes} alt={system.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 right-4">
                        <Badge variant="secondary" className="shadow-xs bg-card/90 text-card-foreground backdrop-blur-xs">
                          {system.tag}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-4">
                      <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-1">
                        {system.iconName === "Activity" ? <Activity className="size-4 shrink-0" /> : <Zap className="size-4 shrink-0" />}
                        <span>{system.subtitle}</span>
                      </div>
                      <CardTitle className="text-2xl sm:text-3xl font-bold text-foreground">
                        {system.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground mt-2 leading-normal">
                        {system.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3 pb-6">
                      <div className="space-y-2.5 pt-2 border-t border-border">
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{system.bullet1}</span>
                        </div>
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{system.bullet2}</span>
                        </div>
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{system.bullet3}</span>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6 flex items-center justify-between border-t border-border/50">
                    <Button asChild variant="default" className="w-full sm:w-auto font-medium">
                      <Link to={system.linkPath}>
                        Explore {system.id === "raman" ? "Raman" : "NIR"} Solutions
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>
      </div>
    </section>;
}